import { Gauge } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { CallStats, Usage } from "@/lib/types";
import { ACTION_BUTTON } from "./actionStyles";

function seconds(ms: number) {
  return ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`;
}

function rate(v: number | undefined) {
  return v === undefined ? "–" : v >= 100 ? Math.round(v).toString() : v.toFixed(1);
}

/** Totals over the model calls of one answer (several when tools were used). */
export function summarize(calls: CallStats[]) {
  const output = calls.reduce((n, c) => n + (c.completion_tokens || 0), 0);
  const measured = calls.some((c) => c.source !== "server");
  // Generation speed weighted by tokens; prompt speed of the first call (later ones are mostly cached).
  const weighted = calls.filter((c) => c.tg_tps && c.completion_tokens > 1);
  const tokens = weighted.reduce((n, c) => n + c.completion_tokens, 0);
  const tg = tokens ? weighted.reduce((n, c) => n + c.completion_tokens * (c.tg_tps ?? 0), 0) / tokens : undefined;
  const queue = calls.some((c) => c.queue_ms !== undefined) ? calls.reduce((n, c) => n + (c.queue_ms ?? 0), 0) : undefined;
  return { output, tg, pp: calls[0]?.pp_tps, ttft: calls[0]?.ttft_ms, serverTtft: calls[0]?.server_ttft_ms, queue, measured };
}

/** A small "how long and how fast" line under an answer; click for the details per model call. */
export default function AnswerStats({ usage }: { usage?: Usage | null }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const stats = usage?.stats;
  if (!stats?.calls?.length) return null;
  const s = summarize(stats.calls);
  const approx = s.measured ? "≈" : "";
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${ACTION_BUTTON} gap-1 text-xs tabular-nums`}
        aria-expanded={open}
        title="Answer statistics"
      >
        <Gauge size={14} />
        {stats.duration_ms !== undefined && <span>{seconds(stats.duration_ms)}</span>}
        {s.tg !== undefined && <span className="hidden sm:inline">· {approx}{rate(s.tg)} tok/s</span>}
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-20 mb-1 w-72 rounded-lg border border-zinc-200 bg-white p-3 text-xs shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 tabular-nums">
            {stats.duration_ms !== undefined && (
              <>
                <dt className="text-zinc-500">Total time</dt>
                <dd>{seconds(stats.duration_ms)}</dd>
              </>
            )}
            {s.ttft !== undefined && (
              <>
                <dt className="text-zinc-500">First token</dt>
                <dd>
                  {seconds(s.ttft)}
                  {s.serverTtft !== undefined && <span className="text-zinc-500"> (server {seconds(s.serverTtft)})</span>}
                </dd>
              </>
            )}
            {s.queue !== undefined && (
              <>
                <dt className="text-zinc-500">Queued</dt>
                <dd>{s.queue < 1000 ? `${s.queue.toFixed(s.queue < 10 ? 1 : 0)} ms` : seconds(s.queue)}</dd>
              </>
            )}
            <dt className="text-zinc-500">Generation (tg)</dt>
            <dd>{approx}{rate(s.tg)} tok/s</dd>
            <dt className="text-zinc-500">Prompt (pp)</dt>
            <dd>{approx}{rate(s.pp)} tok/s</dd>
            <dt className="text-zinc-500">Output tokens</dt>
            <dd>{s.output}</dd>
          </dl>
          {stats.calls.length > 1 && (
            <table className="mt-2 w-full tabular-nums">
              <thead className="text-zinc-500">
                <tr>
                  <th className="text-left font-normal">Call</th>
                  <th className="text-right font-normal">prompt</th>
                  <th className="text-right font-normal">out</th>
                  <th className="text-right font-normal">pp</th>
                  <th className="text-right font-normal">tg</th>
                </tr>
              </thead>
              <tbody>
                {stats.calls.map((c, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td className="text-right">{c.prompt_tokens}</td>
                    <td className="text-right">{c.completion_tokens}</td>
                    <td className="text-right">{rate(c.pp_tps)}</td>
                    <td className="text-right">{rate(c.tg_tps)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {stats.calls.some((c) => c.draft_n) && (
            <p className="mt-2 text-zinc-500">
              Draft tokens accepted: {stats.calls.reduce((n, c) => n + (c.draft_accepted || 0), 0)} of{" "}
              {stats.calls.reduce((n, c) => n + (c.draft_n || 0), 0)}
            </p>
          )}
          <p className="mt-2 text-zinc-500">
            {s.measured
              ? "≈ measured here (includes network and queueing): pp from time to first token, tg from first to last token."
              : "Reported by the model server."}
          </p>
        </div>
      )}
    </div>
  );
}
